import * as THREE from "three";
import { LANE_POSITIONS } from "@shared/constants";
import type { Path } from "./Path";

export type ObstacleKind =
  | "barrier"
  | "crate"
  | "lava"
  | "log"
  | "swing-log"
  | "boulder"
  | "spike-roller"
  | "sweeper";

export interface ObstacleUpdateArgs {
  dt: number;
  now: number;
  playerZ: number;
}

export interface ObstaclePassArgs {
  jumping: boolean;
  sliding: boolean;
}

export interface ObstacleImpactArgs {
  now: number;
  playerS: number;
  playerLaneOffset: number;
  playerForwardVelocity: number;
}

export interface ObstacleContactArgs {
  playerS: number;
  playerLaneOffset: number;
  playerY: number;
  playerRadius: number;
  playerForwardVelocity: number;
  playerLateralVelocity: number;
}

export interface ObstacleContact {
  normalS: number;
  normalL: number;
  penetration: number;
  relativeSpeed: number;
  obstacleVelocityS: number;
  obstacleVelocityL: number;
  isForceful: boolean;
}

export interface ObstacleController {
  id: number;
  kind: ObstacleKind;
  mesh: THREE.Object3D;
  resolved: boolean;
  mass?: number;
  getLateralOffset?(): number;
  lanes(): number[];
  getZ(): number;
  update(args: ObstacleUpdateArgs): void;
  passes(args: ObstaclePassArgs): boolean;
  hasForceImpact?(): boolean;
  impact?(args: ObstacleImpactArgs): void;
  applyPhysicsImpulse?(forward: number, lateral: number): void;
  getContact?(args: ObstacleContactArgs): ObstacleContact | null;
}

interface BaseOpts {
  id: number;
  z: number; // arc-length s along the path
  lane: number;
  path?: Path;
}

/** Place a mesh at path arc length `s`, optionally with a lateral lane offset. */
function placeOnPath(mesh: THREE.Object3D, path: Path | undefined, s: number, laneOffset = 0) {
  if (!path) {
    mesh.position.set(laneOffset, 0, s);
    return;
  }
  const pos = path.positionAt(s, laneOffset);
  mesh.position.copy(pos);
  mesh.rotation.y = path.yawAt(s);
}

const PALETTE = {
  barrier: 0xef476f,
  barrierPost: 0x222222,
  crate: 0xb3753a,
  crateBand: 0x6f4624,
  lavaCool: 0x8a2c11,
  lavaHot: 0xff7a2b,
  log: 0x6f4a2a,
  logLeaf: 0x2f7d3a,
  swingLog: 0x8b5a2b,
  swingRope: 0x32251b,
  boulder: 0x6e6a64,
  spike: 0x9aa3b2,
  spikePoint: 0x444444,
  sweeperArm: 0x8bdc2d,
  sweeperPost: 0xff2ca4,
} as const;

/* ── BARRIER ────────────────────────────────────────────────────────────── */
class Barrier implements ObstacleController {
  kind: ObstacleKind = "barrier";
  mesh = new THREE.Group();
  resolved = false;
  mass = 8;
  private s: number;
  constructor(public id: number, z: number, public lane: number, path?: Path) {
    this.s = z;
    placeOnPath(this.mesh, path, z, LANE_POSITIONS[lane]!);
    const bar = new THREE.Mesh(
      new THREE.BoxGeometry(1.6, 0.7, 0.35),
      new THREE.MeshStandardMaterial({ color: PALETTE.barrier }),
    );
    bar.position.y = 1.4;
    bar.castShadow = true;
    this.mesh.add(bar);
    for (const x of [-0.7, 0.7]) {
      const post = new THREE.Mesh(
        new THREE.BoxGeometry(0.18, 1.5, 0.18),
        new THREE.MeshStandardMaterial({ color: PALETTE.barrierPost }),
      );
      post.position.set(x, 0.75, 0);
      post.castShadow = true;
      this.mesh.add(post);
    }
  }
  lanes() { return [this.lane]; }
  getZ() { return this.s; }
  update() {}
  passes({ sliding }: ObstaclePassArgs) { return sliding; }
}

/* ── CRATE ──────────────────────────────────────────────────────────────── */
class Crate implements ObstacleController {
  kind: ObstacleKind = "crate";
  mesh = new THREE.Group();
  resolved = false;
  mass = 2.5;
  private s: number;
  constructor(public id: number, z: number, public lane: number, path?: Path) {
    this.s = z;
    placeOnPath(this.mesh, path, z, LANE_POSITIONS[lane]!);
    const box = new THREE.Mesh(
      new THREE.BoxGeometry(1.4, 1.4, 1.4),
      new THREE.MeshStandardMaterial({ color: PALETTE.crate }),
    );
    box.position.y = 0.7;
    box.castShadow = true;
    this.mesh.add(box);
    const bandMat = new THREE.MeshStandardMaterial({ color: PALETTE.crateBand });
    const bandGeo = new THREE.BoxGeometry(1.42, 0.12, 0.12);
    for (const y of [0.3, 1.1]) {
      const b = new THREE.Mesh(bandGeo, bandMat);
      b.position.y = y;
      this.mesh.add(b);
    }
  }
  lanes() { return [this.lane]; }
  getZ() { return this.s; }
  update() {}
  passes({ jumping }: ObstaclePassArgs) { return jumping; }
}

/* ── LAVA ───────────────────────────────────────────────────────────────── */
class Lava implements ObstacleController {
  kind: ObstacleKind = "lava";
  mesh = new THREE.Group();
  resolved = false;
  mass = 20;
  private s: number;
  /** Forced to center lane (2). Pool spans [1,2,3]; ends never overhang the path. */
  public centerLane = 2;
  constructor(public id: number, z: number, _lane: number, path?: Path) {
    this.s = z;
    placeOnPath(this.mesh, path, z, 0);
    const width = 7;
    const mat = new THREE.MeshStandardMaterial({
      color: PALETTE.lavaHot,
      emissive: PALETTE.lavaHot,
      emissiveIntensity: 0.7,
    });
    const pool = new THREE.Mesh(new THREE.BoxGeometry(width, 0.15, 1.4), mat);
    pool.position.set(0, 0.08, 0);
    this.mesh.add(pool);
    const rimMat = new THREE.MeshStandardMaterial({ color: PALETTE.lavaCool });
    for (const dz of [-0.75, 0.75]) {
      const rim = new THREE.Mesh(new THREE.BoxGeometry(width, 0.25, 0.18), rimMat);
      rim.position.set(0, 0.13, dz);
      this.mesh.add(rim);
    }
  }
  lanes() { return [1, 2, 3]; }
  getZ() { return this.s; }
  update() {}
  passes({ jumping }: ObstaclePassArgs) { return jumping; }
}

/* ── FALLING LOG (or just a standing tree, randomized) ──────────────────── */
class FallingLog implements ObstacleController {
  kind: ObstacleKind = "log";
  mesh = new THREE.Group();
  resolved = false;
  mass = 7;
  private state: "standing" | "falling" | "bouncing" | "fallen" | "decoration" = "standing";
  private fallT = 0;
  private side: 1 | -1;
  private centerLane: number;
  private willFall: boolean;
  private baseX: number;
  public onImpact: (() => void) | null = null;
  private impactFired = false;
  private s: number;

  constructor(public id: number, z: number, lane: number, path?: Path) {
    this.s = z;
    placeOnPath(this.mesh, path, z, 0);
    this.centerLane = Math.max(1, Math.min(LANE_POSITIONS.length - 2, lane));
    this.side = LANE_POSITIONS[this.centerLane]! < 0 ? -1 : 1;
    // Stand right at the path edge (was 7.5 — too far off, looked detached).
    // 6.0 sits just outside the parapet wall; falling 7m trunk reaches center.
    this.baseX = this.side * 6.0;
    this.willFall = Math.random() < 0.6;
    if (!this.willFall) this.state = "decoration";

    const trunkGeo = new THREE.CylinderGeometry(0.45, 0.55, 7, 10);
    const trunkMat = new THREE.MeshStandardMaterial({ color: PALETTE.log });
    const trunk = new THREE.Mesh(trunkGeo, trunkMat);
    trunk.castShadow = true;
    trunk.position.set(this.baseX, 3.5, 0);
    this.mesh.add(trunk);

    const crown = new THREE.Mesh(
      new THREE.IcosahedronGeometry(1.6, 0),
      new THREE.MeshStandardMaterial({ color: PALETTE.logLeaf, flatShading: true }),
    );
    crown.position.set(this.baseX, 7, 0);
    crown.castShadow = true;
    this.mesh.add(crown);
  }
  lanes() {
    if (this.state === "standing" || this.state === "decoration") return [];
    return [this.centerLane - 1, this.centerLane, this.centerLane + 1].filter(
      (l) => l >= 0 && l < LANE_POSITIONS.length,
    );
  }
  getZ() { return this.s; }

  private rotateAroundBase(angle: number) {
    const cs = Math.cos(angle);
    const sn = Math.sin(angle);
    this.mesh.children.forEach((c) => {
      if (c.userData.baseX === undefined) {
        c.userData.baseX = c.position.x;
        c.userData.baseY = c.position.y;
      }
      const localX = c.userData.baseX - this.baseX;
      const localY = c.userData.baseY;
      c.position.x = this.baseX + localX * cs - localY * sn;
      c.position.y = localX * sn + localY * cs;
      c.rotation.z = angle;
    });
  }

  update({ dt, now, playerZ }: ObstacleUpdateArgs) {
    if (this.state === "decoration") return;
    const dist = this.s - playerZ;
    if (this.state === "standing" && dist < 18 && dist > 4) {
      this.state = "falling";
      this.fallT = 0;
    }
    if (this.state === "falling" || this.state === "bouncing") {
      this.fallT += dt;
      const fallDur = 0.55;
      let angle: number;
      if (this.fallT < fallDur) {
        const t = this.fallT / fallDur;
        const eased = t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;
        angle = eased * (Math.PI / 2) * this.side;
      } else {
        if (!this.impactFired) {
          this.impactFired = true;
          this.onImpact?.();
        }
        const t = Math.min(1, (this.fallT - fallDur) / 0.5);
        const decay = Math.exp(-3 * t);
        const wobble = Math.sin(t * Math.PI * 4) * 0.08 * decay;
        angle = (Math.PI / 2 + wobble) * this.side;
        this.state = "bouncing";
        if (t >= 1) this.state = "fallen";
      }
      this.rotateAroundBase(angle);
    }
  }
  passes({ jumping }: ObstaclePassArgs) {
    if (this.state === "standing" || this.state === "decoration") return true;
    if (this.state === "falling" || this.state === "bouncing") return false;
    return jumping;
  }
  hasForceImpact() {
    return this.state === "falling" || this.state === "bouncing";
  }
}

/* ── SWINGING LOG ───────────────────────────────────────────────────────── */
class SwingingLog implements ObstacleController {
  kind: ObstacleKind = "swing-log";
  mesh = new THREE.Group();
  resolved = false;
  mass = 90;
  private frame = new THREE.Group();
  private log = new THREE.Group();
  private s: number;
  private phase = 0;
  private swing = 0;
  private swingVel = 0;
  private amplitude = 2.95;
  private angularVelocity = 1.55;
  private logHalfLength = 2.35;
  private logRadius = 0.42;

  constructor(public id: number, z: number, _lane: number, path?: Path) {
    this.s = z;
    placeOnPath(this.mesh, path, z, 0);

    const postMat = new THREE.MeshStandardMaterial({ color: 0x7c3aed, roughness: 0.45 });
    const ropeMat = new THREE.MeshStandardMaterial({ color: PALETTE.swingRope, roughness: 0.7 });
    for (const x of [-2.85, 2.85]) {
      const post = new THREE.Mesh(new THREE.BoxGeometry(0.32, 4.2, 0.32), postMat);
      post.position.set(x, 2.1, 0);
      post.castShadow = true;
      this.frame.add(post);
    }
    const beam = new THREE.Mesh(new THREE.BoxGeometry(6.3, 0.34, 0.34), postMat);
    beam.position.set(0, 4.12, 0);
    beam.castShadow = true;
    this.frame.add(beam);
    this.mesh.add(this.frame);

    for (const zOff of [-1.65, 1.65]) {
      const rope = new THREE.Mesh(new THREE.CylinderGeometry(0.045, 0.045, 2.9, 8), ropeMat);
      rope.position.set(0, 2.45, zOff);
      rope.castShadow = true;
      this.log.add(rope);
    }
    const trunk = new THREE.Mesh(
      new THREE.CylinderGeometry(this.logRadius, this.logRadius, this.logHalfLength * 2, 18),
      new THREE.MeshStandardMaterial({ color: PALETTE.swingLog, roughness: 0.58 }),
    );
    trunk.rotation.x = Math.PI / 2;
    trunk.position.y = 0.95;
    trunk.castShadow = true;
    trunk.receiveShadow = true;
    this.log.add(trunk);
    this.mesh.add(this.log);
  }

  lanes() { return [1, 2, 3]; }
  getZ() { return this.s; }
  update({ dt, now }: ObstacleUpdateArgs) {
    this.phase += dt * this.angularVelocity;
    this.swing = Math.sin(this.phase) * this.amplitude;
    this.swingVel = Math.cos(this.phase) * this.amplitude * this.angularVelocity;
    this.log.position.x = this.swing;
    this.log.rotation.z = -this.swing * 0.12;
    this.log.position.y = 0.12 + Math.abs(Math.sin(this.phase)) * 0.1;
    this.frame.rotation.y = Math.sin(now * 0.0011) * 0.015;
  }
  passes() { return false; }
  hasForceImpact() { return true; }
  getContact({
    playerS,
    playerLaneOffset,
    playerY,
    playerRadius,
    playerLateralVelocity,
  }: ObstacleContactArgs): ObstacleContact | null {
    if (playerY > 1.75) return null;
    const px = playerLaneOffset;
    const pz = playerS - this.s;
    const closestZ = THREE.MathUtils.clamp(pz, -this.logHalfLength, this.logHalfLength);
    const dx = px - this.swing;
    const dz = pz - closestZ;
    const radius = this.logRadius + playerRadius;
    const distSq = dx * dx + dz * dz;
    if (distSq > radius * radius) return null;
    const dist = Math.sqrt(Math.max(0.0001, distSq));
    const normalL = dx / dist;
    const normalS = dz / dist;
    const relL = this.swingVel - playerLateralVelocity;
    const relativeSpeed = Math.max(0, relL * normalL);
    return {
      normalS,
      normalL,
      penetration: radius - dist,
      relativeSpeed,
      obstacleVelocityS: 0,
      obstacleVelocityL: this.swingVel,
      isForceful: relativeSpeed > 1.0 || Math.abs(this.swingVel) > 2.2,
    };
  }
}

/* ── ROLLING BOULDER ────────────────────────────────────────────────────── */
class RollingBoulder implements ObstacleController {
  kind: ObstacleKind = "boulder";
  mesh: THREE.Group;
  resolved = false;
  mass = 18;
  private ball = new THREE.Group();
  private body: THREE.Mesh;
  private cannon = new THREE.Group();
  private state: "waiting" | "launched" | "rolling" = "waiting";
  private velocityS = 0;
  private velocityL = 0;
  private lateralOffset: number;
  private cannonOffset: number;
  private airY = 0;
  private airVel = 0;
  private lastGroundY = 0;
  private nextFireAt = 0;
  private firedAt = 0;
  private startS: number;
  private s: number;
  private path?: Path;
  private readonly fireIntervalMs = 5000;
  constructor(public id: number, z: number, public lane: number, path?: Path) {
    this.mesh = new THREE.Group();
    this.startS = z;
    this.s = z + 22;
    this.path = path;
    const laneOffset = LANE_POSITIONS[lane]!;
    this.cannonOffset = laneOffset < 0 ? -6.7 : 6.7;
    this.lateralOffset = this.cannonOffset;
    this.body = new THREE.Mesh(
      new THREE.IcosahedronGeometry(1.0, 1),
      new THREE.MeshStandardMaterial({ color: PALETTE.boulder, flatShading: true }),
    );
    this.body.position.y = 1.0;
    this.body.castShadow = true;
    this.ball.add(this.body);
    this.ball.visible = false;
    this.mesh.add(this.ball);

    const cannonMat = new THREE.MeshStandardMaterial({ color: 0x293241, roughness: 0.42, metalness: 0.35 });
    const barrel = new THREE.Mesh(new THREE.CylinderGeometry(0.48, 0.62, 2.2, 18), cannonMat);
    barrel.rotation.z = Math.PI / 2;
    barrel.position.set(this.cannonOffset < 0 ? 0.62 : -0.62, 1.2, 0);
    barrel.castShadow = true;
    this.cannon.add(barrel);
    const base = new THREE.Mesh(new THREE.BoxGeometry(1.5, 0.45, 1.2), cannonMat);
    base.position.set(0, 0.25, 0);
    base.castShadow = true;
    this.cannon.add(base);
    this.mesh.add(this.cannon);
    this.placeBody();
    this.lastGroundY = this.ball.position.y;
    this.placeCannon();
  }
  getLateralOffset() { return this.lateralOffset; }
  lanes() {
    if (Math.abs(this.lateralOffset) > 5.6) return [];
    const nearest = nearestLane(this.lateralOffset);
    const lanes = [nearest];
    const laneCenter = LANE_POSITIONS[nearest]!;
    if (Math.abs(this.lateralOffset - laneCenter) > 0.75) {
      const dir = this.lateralOffset > laneCenter ? 1 : -1;
      const next = nearest + dir;
      if (next >= 0 && next < LANE_POSITIONS.length) lanes.push(next);
    }
    return lanes;
  }
  getZ() { return this.s; }
  update({ dt, now, playerZ }: ObstacleUpdateArgs) {
    const activeRange = this.startS - playerZ < 32 && playerZ < this.startS + 150;
    if (this.state === "waiting") {
      this.s = this.startS + 22;
      this.lateralOffset = this.cannonOffset;
      this.airY = 0;
      this.airVel = 0;
      this.placeBody();
      if (activeRange && now >= this.nextFireAt) this.fire();
    }
    if (this.state === "launched" || this.state === "rolling") {
      const targetL = LANE_POSITIONS[this.lane]!;
      const prevGroundY = this.lastGroundY;
      this.s += this.velocityS * dt;
      this.lateralOffset += this.velocityL * dt;
      if (this.state === "launched" && Math.abs(this.lateralOffset - targetL) < 0.55) {
        this.state = "rolling";
        this.lateralOffset = targetL;
        this.velocityL *= 0.55;
      }
      if (this.state === "rolling") {
        const slope = this.path?.heightSlopeAt(this.s) ?? 0;
        this.velocityS += -slope * 22 * dt;
        this.velocityS *= Math.exp(-0.34 * dt);
        this.velocityL *= Math.exp(-0.7 * dt);
        if (Math.abs(this.velocityS) < 0.05) this.velocityS = 0;
        if (Math.abs(this.velocityL) < 0.05) this.velocityL = 0;
      }
      this.bounceWalls();
      this.updateVertical(dt, prevGroundY);
      this.placeBody();
      this.body.rotation.x += (this.velocityS * dt) / 1.0;
      this.body.rotation.z -= (this.velocityL * dt) / 1.0;
    }
    if (this.state !== "waiting" && now - this.firedAt >= this.fireIntervalMs) {
      this.resetForNextShot(now);
    } else if (this.state !== "waiting" && (this.s < playerZ - 8 || Math.abs(this.lateralOffset) > 7.4)) {
      this.resetForNextShot(now);
    }
  }
  passes() { return false; }
  hasForceImpact() {
    return this.state !== "waiting" && (Math.abs(this.velocityS) > 3 || Math.abs(this.velocityL) > 3);
  }
  getContact({
    playerS,
    playerLaneOffset,
    playerY,
    playerRadius,
    playerForwardVelocity,
    playerLateralVelocity,
  }: ObstacleContactArgs): ObstacleContact | null {
    if (this.state === "waiting") return null;
    if (playerY > 1.65 + this.airY) return null;
    const dz = playerS - this.s;
    const dl = playerLaneOffset - this.lateralOffset;
    const radius = 1.0 + playerRadius;
    const distSq = dz * dz + dl * dl;
    if (distSq > radius * radius) return null;
    const dist = Math.sqrt(Math.max(0.0001, distSq));
    const normalS = dz / dist;
    const normalL = dl / dist;
    const relS = this.velocityS - playerForwardVelocity;
    const relL = this.velocityL - playerLateralVelocity;
    const relativeSpeed = Math.max(0, relS * normalS + relL * normalL);
    return {
      normalS,
      normalL,
      penetration: radius - dist,
      relativeSpeed,
      obstacleVelocityS: this.velocityS,
      obstacleVelocityL: this.velocityL,
      isForceful: relativeSpeed > 2.4 || Math.hypot(this.velocityS, this.velocityL) > 6,
    };
  }
  impact({ playerForwardVelocity, playerLaneOffset }: ObstacleImpactArgs) {
    if (this.state === "waiting") return;
    const side = THREE.MathUtils.clamp(this.lateralOffset - playerLaneOffset, -1.2, 1.2);
    const currentSpeed = Math.max(8.5, Math.abs(this.velocityS));
    const playerPush = Math.max(0, playerForwardVelocity) * 0.08;
    this.velocityS = this.velocityS <= 0
      ? -Math.max(7.5, currentSpeed * 0.76 - playerPush)
      : Math.max(3, this.velocityS * 0.35);
    this.velocityL += side * 2.6;
  }
  applyPhysicsImpulse(forward: number, lateral: number) {
    if (this.state === "waiting") this.fire();
    this.velocityS += forward / this.mass;
    this.velocityL += lateral / this.mass;
  }
  private fire() {
    const laneOffset = LANE_POSITIONS[this.lane]!;
    this.state = "launched";
    this.resolved = false;
    this.firedAt = performance.now();
    this.s = this.startS + 22;
    this.lateralOffset = this.cannonOffset;
    this.airY = 0;
    this.airVel = 0;
    this.lastGroundY = this.path?.heightAt(this.s) ?? 0;
    const slope = this.path?.heightSlopeAt(this.s) ?? 0;
    this.velocityS = -5 - Math.max(0, slope) * 8;
    this.velocityL = Math.sign(laneOffset - this.cannonOffset) * 18;
    this.ball.visible = true;
    this.placeBody();
  }
  private resetForNextShot(now: number) {
    this.state = "waiting";
    this.resolved = true;
    this.velocityS = 0;
    this.velocityL = 0;
    this.ball.visible = false;
    this.nextFireAt = Math.max(now, this.firedAt + this.fireIntervalMs);
  }
  private bounceWalls() {
    const half = 5.35;
    if (this.lateralOffset < -half) {
      this.lateralOffset = -half;
      this.velocityL = Math.abs(this.velocityL) * 0.82;
    } else if (this.lateralOffset > half) {
      this.lateralOffset = half;
      this.velocityL = -Math.abs(this.velocityL) * 0.82;
    }
  }
  private placeBody() {
    placeOnPath(this.ball, this.path, this.s, this.lateralOffset);
    this.ball.position.y += this.airY;
  }
  private placeCannon() {
    placeOnPath(this.cannon, this.path, this.startS + 22, this.cannonOffset);
    this.cannon.rotation.y += this.cannonOffset < 0 ? -Math.PI / 2 : Math.PI / 2;
  }
  private updateVertical(dt: number, prevGroundY: number) {
    const groundY = this.path?.heightAt(this.s) ?? 0;
    const groundDelta = groundY - prevGroundY;
    const steepWall = groundDelta > 0.7 && this.airY < 0.15;
    if (steepWall) {
      this.s -= this.velocityS * dt * 0.9;
      this.velocityS = Math.abs(this.velocityS) * 0.18;
      this.velocityL *= 0.45;
      this.airVel = 2.2;
      this.airY = 0.08;
      return;
    }
    if (groundDelta < -0.55 && this.airY < 0.1) {
      this.airY += -groundDelta * 0.45;
      this.airVel = Math.max(this.airVel, 1.2);
    }
    if (this.airY > 0 || this.airVel !== 0) {
      this.airVel -= 14 * dt;
      this.airY += this.airVel * dt;
      if (this.airY <= 0) {
        this.airY = 0;
        this.airVel = Math.abs(this.airVel) * 0.28;
        this.velocityS *= 0.82;
        this.velocityL *= 0.78;
        if (this.airVel < 1.1) this.airVel = 0;
      }
    }
    this.lastGroundY = groundY;
  }
}

/* ── SPIKE ROLLER ───────────────────────────────────────────────────────── */
class SpikeRoller implements ObstacleController {
  kind: ObstacleKind = "spike-roller";
  mesh = new THREE.Group();
  resolved = false;
  mass = 10;
  private roller: THREE.Group;
  private s: number;
  constructor(public id: number, z: number, _lane: number, path?: Path) {
    this.s = z;
    placeOnPath(this.mesh, path, z, 0);
    this.roller = new THREE.Group();
    // Always centered on the path so end posts never overhang on a corner.
    this.roller.position.set(0, 1.0, 0);
    const cyl = new THREE.Mesh(
      new THREE.CylinderGeometry(0.7, 0.7, 6.5, 14),
      new THREE.MeshStandardMaterial({ color: PALETTE.spike }),
    );
    cyl.rotation.z = Math.PI / 2;
    cyl.castShadow = true;
    this.roller.add(cyl);
    const spikeMat = new THREE.MeshStandardMaterial({ color: PALETTE.spikePoint });
    const spikeGeo = new THREE.ConeGeometry(0.18, 0.5, 6);
    for (let i = 0; i < 8; i++) {
      const angle = (i / 8) * Math.PI * 2;
      for (let xPos = -3; xPos <= 3; xPos += 0.85) {
        const s = new THREE.Mesh(spikeGeo, spikeMat);
        s.position.set(xPos, Math.sin(angle) * 0.95, Math.cos(angle) * 0.95);
        s.rotation.set(Math.PI / 2 + angle, 0, Math.PI / 2);
        this.roller.add(s);
      }
    }
    this.mesh.add(this.roller);
    const postMat = new THREE.MeshStandardMaterial({ color: 0x444444 });
    for (const x of [-3.5, 3.5]) {
      const post = new THREE.Mesh(new THREE.BoxGeometry(0.4, 2.2, 0.4), postMat);
      post.position.set(x, 1.1, 0);
      this.mesh.add(post);
    }
  }
  lanes() { return [1, 2, 3]; }
  getZ() { return this.s; }
  update({ dt }: ObstacleUpdateArgs) {
    this.roller.rotation.x += dt * 4;
  }
  passes({ jumping, sliding }: ObstaclePassArgs) {
    return jumping || sliding;
  }
}

/* ── ROTATING SWEEPER ───────────────────────────────────────────────────── */
class RotatingSweeper implements ObstacleController {
  kind: ObstacleKind = "sweeper";
  mesh = new THREE.Group();
  resolved = false;
  mass = 14;
  private armPivot = new THREE.Group();
  private s: number;
  private spin = 0;
  private angularVelocity = 2.45;
  private armHalfLength = 5.2;
  private armRadius = 0.28;

  constructor(public id: number, z: number, _lane: number, path?: Path) {
    this.s = z;
    placeOnPath(this.mesh, path, z, 0);

    const baseMat = new THREE.MeshStandardMaterial({ color: 0x6d28d9, roughness: 0.45, metalness: 0.06 });
    const base = new THREE.Mesh(new THREE.CylinderGeometry(1.25, 1.45, 0.45, 24), baseMat);
    base.position.y = 0.22;
    base.castShadow = true;
    base.receiveShadow = true;
    this.mesh.add(base);

    const post = new THREE.Mesh(
      new THREE.CylinderGeometry(0.55, 0.66, 2.5, 24),
      new THREE.MeshStandardMaterial({ color: PALETTE.sweeperPost, roughness: 0.42 }),
    );
    post.position.y = 1.35;
    post.castShadow = true;
    this.mesh.add(post);

    const stripeMat = new THREE.MeshStandardMaterial({ color: 0xffffff, roughness: 0.52 });
    for (let y = 0.55; y < 2.45; y += 0.48) {
      const stripe = new THREE.Mesh(new THREE.CylinderGeometry(0.58, 0.68, 0.12, 24), stripeMat);
      stripe.position.y = y;
      this.mesh.add(stripe);
    }

    this.armPivot.position.y = 0.82;
    const arm = new THREE.Mesh(
      new THREE.CapsuleGeometry(0.28, 9.8, 12, 24),
      new THREE.MeshStandardMaterial({ color: PALETTE.sweeperArm, roughness: 0.5 }),
    );
    arm.rotation.z = Math.PI / 2;
    arm.castShadow = true;
    arm.receiveShadow = true;
    this.armPivot.add(arm);

    const tipMat = new THREE.MeshStandardMaterial({ color: 0xb8ff5a, roughness: 0.45 });
    for (const x of [-5.2, 5.2]) {
      const cap = new THREE.Mesh(new THREE.SphereGeometry(0.33, 16, 12), tipMat);
      cap.position.x = x;
      cap.castShadow = true;
      this.armPivot.add(cap);
    }
    this.mesh.add(this.armPivot);
  }

  lanes() { return [0, 1, 2, 3, 4]; }
  getZ() { return this.s; }
  getLateralOffset() { return Math.sin(this.spin) * 1.2; }
  update({ dt }: ObstacleUpdateArgs) {
    this.spin += dt * this.angularVelocity;
    this.armPivot.rotation.y = this.spin;
  }
  passes({ jumping }: ObstaclePassArgs) {
    return jumping;
  }
  hasForceImpact() {
    return true;
  }
  getContact({
    playerS,
    playerLaneOffset,
    playerY,
    playerRadius,
    playerForwardVelocity,
    playerLateralVelocity,
  }: ObstacleContactArgs): ObstacleContact | null {
    // The green arm is a moving capsule in obstacle-local (lateral, course)
    // space. Contact is checked against the actual bar, not broad lanes.
    if (playerY > 1.25) return null;
    const px = playerLaneOffset;
    const pz = playerS - this.s;
    const ux = Math.cos(this.spin);
    const uz = -Math.sin(this.spin);
    const along = THREE.MathUtils.clamp(px * ux + pz * uz, -this.armHalfLength, this.armHalfLength);
    const closestX = ux * along;
    const closestZ = uz * along;
    const dx = px - closestX;
    const dz = pz - closestZ;
    const radius = this.armRadius + playerRadius;
    const distSq = dx * dx + dz * dz;
    if (distSq > radius * radius) return null;
    const dist = Math.sqrt(Math.max(0.0001, distSq));

    // Tangential velocity of the bar at the contact point: w × r.
    const barVelL = -this.angularVelocity * closestZ;
    const barVelS = this.angularVelocity * closestX;
    const relL = barVelL - playerLateralVelocity;
    const relS = barVelS - playerForwardVelocity;
    let normalL = dx / dist;
    let normalS = dz / dist;

    // If the player is almost exactly centered on the arm, use the direction
    // the bar is sweeping so they are carried by the hit instead of jittering.
    if (dist < 0.08) {
      const speed = Math.hypot(barVelL, barVelS);
      if (speed > 0.001) {
        normalL = barVelL / speed;
        normalS = barVelS / speed;
      }
    }
    const relativeSpeed = Math.max(0, relL * normalL + relS * normalS);
    return {
      normalS,
      normalL,
      penetration: radius - dist,
      relativeSpeed,
      obstacleVelocityS: barVelS,
      obstacleVelocityL: barVelL,
      isForceful: relativeSpeed > 2.0 || Math.hypot(barVelL, barVelS) > 3.4,
    };
  }
}

/* ── factory ─────────────────────────────────────────────────────────────── */
export function createObstacle(kind: ObstacleKind, opts: BaseOpts): ObstacleController {
  switch (kind) {
    case "barrier": return new Barrier(opts.id, opts.z, opts.lane, opts.path);
    case "crate": return new Crate(opts.id, opts.z, opts.lane, opts.path);
    case "lava": return new Lava(opts.id, opts.z, opts.lane, opts.path);
    case "log": return new FallingLog(opts.id, opts.z, opts.lane, opts.path);
    case "swing-log": return new SwingingLog(opts.id, opts.z, opts.lane, opts.path);
    case "boulder": return new RollingBoulder(opts.id, opts.z, opts.lane, opts.path);
    case "spike-roller": return new SpikeRoller(opts.id, opts.z, opts.lane, opts.path);
    case "sweeper": return new RotatingSweeper(opts.id, opts.z, opts.lane, opts.path);
  }
}

export function isFallingLog(o: ObstacleController): o is FallingLog {
  return o.kind === "log";
}

function nearestLane(offset: number): number {
  let bestLane = 0;
  let bestDist = Infinity;
  for (let i = 0; i < LANE_POSITIONS.length; i++) {
    const d = Math.abs(offset - LANE_POSITIONS[i]!);
    if (d < bestDist) {
      bestDist = d;
      bestLane = i;
    }
  }
  return bestLane;
}
